import { Box, Stack } from '@mui/material';
import {
  GenerateFormFields_New,
  getFieldsRules_New,
  RenderType,
  TFormFieldsAndRules_New,
  useCustomForm,
} from '@verifime/components';
import tokens from '@verifime/design-tokens';
import { TDocument, TIdentityDocumentType, validationRules } from '@verifime/utils';
import { useEffect, useState } from 'react';
import DocumentInstructions from './DocumentInstructions';
import ExtraInfo from './ExtraInfo';

interface TDocumentTypeSelectionProps {
  defaultDocumentType: TIdentityDocumentType;
  fieldName: string;
  supportedDocumentTypes: TDocument[];
  onDocumentTypeChange: (documentType: TIdentityDocumentType) => void;
  dataTestId?: string;
}

export default function DocumentTypeSelection({
  defaultDocumentType,
  fieldName,
  supportedDocumentTypes,
  onDocumentTypeChange,
  dataTestId,
}: Readonly<TDocumentTypeSelectionProps>) {
  const [selectedDocumentType, setSelectedDocumentType] =
    useState<TIdentityDocumentType>(defaultDocumentType);

  useEffect(() => {
    setSelectedDocumentType(defaultDocumentType);
  }, [defaultDocumentType]);

  const formFieldsAndRules: TFormFieldsAndRules_New = {
    row1: [
      {
        label: 'Select Document Type',
        fieldName,
        renderType: RenderType.Select,
        rules: validationRules.REQUIRED_STRING_RULES,
        items: supportedDocumentTypes,
      },
    ],
  };

  const {
    control,
    formState: { errors },
  } = useCustomForm({
    schema: getFieldsRules_New(formFieldsAndRules),
  });

  if (!selectedDocumentType) {
    return null;
  }

  return (
    <Box data-testid={dataTestId} data-component="DocumentTypeSelection" sx={{ width: '100%' }}>
      <DocumentInstructions
        documentType={selectedDocumentType}
        dataTestId={`${dataTestId}-instructions`}
      />
      <Stack gap={tokens.spacingXl}>
        {/** TODO: add gap back once we tweak the styles of GenerateFormFields_New  */}
        <Stack>
          <Box
            data-testid={`${dataTestId}-select-document-type`}
            data-component="DocumentTypeSelectField"
          >
            <GenerateFormFields_New
              formFieldsAndRules={formFieldsAndRules}
              control={control}
              errors={errors}
              fieldsOperations={{
                [fieldName]: {
                  onOptionChange: (value: TIdentityDocumentType) => {
                    setSelectedDocumentType(value);
                    onDocumentTypeChange(value);
                  },
                  initValue: selectedDocumentType,
                },
              }}
            />
          </Box>
          <ExtraInfo documentType={selectedDocumentType} dataTestId={`${dataTestId}-extra-info`} />
        </Stack>
      </Stack>
    </Box>
  );
}
